import { FC, SyntheticEvent, useRef, useState } from "react";

import Box from "@mui/material/Box";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateFieldName } from "../../../../hooks/useRateFieldName";
import { ConfirmRateChangeDialog } from "../../../../RateFields/ConfirmRateChangeDialog/ConfirmRateChangeDialog";
import { getRestorableTaxIds } from "../../../../RateFields/TaxesField/helpers";
import { TaxesField } from "../../../../RateFields/TaxesField/TaxesField";
import { RateOptionToggleRow } from "../../RateOptionToggleRow";

import { useConfirmTaxChange } from "./useConfirmTaxChange";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useTicketingTaxes } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useTicketingTaxes";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const TaxRow: FC = () => {
  const { t } = useTranslate();
  const { control, setValue } = useFormContext<StepTicketsFormData>();
  const { taxes } = useTicketingTaxes();

  const fieldName = useRateFieldName("taxIds");
  const taxIds = useWatch({ control, name: fieldName });

  const [isOn, setIsOn] = useState(() => (taxIds?.length ?? 0) > 0);
  const stashedIdsRef = useRef<string[]>([]);
  const { isConfirmed, isDialogOpen, runAfterConfirmation, confirm, cancel } = useConfirmTaxChange();

  const guardTaxPicker = (event: SyntheticEvent) => {
    if (isConfirmed) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    runAfterConfirmation(() => undefined);
  };

  const handleToggle = (checked: boolean) => {
    setIsOn(checked);
    if (checked) {
      const restorableIds = getRestorableTaxIds(stashedIdsRef.current, taxes);
      if (restorableIds.length > 0) {
        setValue(fieldName, restorableIds, { shouldDirty: true });
      }
    } else {
      stashedIdsRef.current = taxIds ?? [];
      setValue(fieldName, [], { shouldDirty: true });
    }
  };

  return (
    <>
      <RateOptionToggleRow
        optionKey="tax"
        checked={isOn}
        onToggle={(checked) => runAfterConfirmation(() => handleToggle(checked))}
        label={t("dashboard", "ticketing.rateOption.tax")}
        tooltip={t("dashboard", "ticketing.taxes.field.tooltip")}
        inlineConfig={
          isOn ? (
            <Box
              sx={{ display: "contents" }}
              onMouseDownCapture={guardTaxPicker}
              onClickCapture={guardTaxPicker}
              onKeyDownCapture={guardTaxPicker}
            >
              <TaxesField />
            </Box>
          ) : undefined
        }
      />
      <ConfirmRateChangeDialog open={isDialogOpen} variant="taxes" onConfirm={confirm} onClose={cancel} />
    </>
  );
};
